import React from 'react';
import { StyleSheet, View, Text, ActivityIndicator, ScrollView, Platform } from 'react-native';
import { useTheme } from '../../../../theme/useTheme';
import EfficiencyVarianceHeatGridCard from './components/EfficiencyVarianceHeatGridCard';
import KCPHTrendCard from './components/KCPHTrendCard';
import LocationLevelEfficiencyTable from './components/LocationLevelEfficiencyTable';
import EmployeeLevelEfficiencyTable from './components/EmployeeLevelEfficiencyTable';
import {
  type ChartDrillLevel,
  type ChartTimeframeGrain,
  parseTimeframeLabel,
} from './crewingAnalyticsStaticData';

import type { VarianceHeatmapResponse, KcphTrendResponse, LocationEfficiencyRow, EmployeeEfficiencyRow, PagedResponse } from './crewingAnalyticsApi';
import type { AsyncState } from './useCrewingAnalytics';

type AsyncStateOf<T> = AsyncState<T>;

interface EfficiencyBreakdownProps {
  drillLevel: ChartDrillLevel;
  timeframe: ChartTimeframeGrain;
  selectedRegions?: string[];
  selectedMarkets?: string[];
  selectedLocations?: string[];
  selectedColumnKeys?: string[];
  apiVarianceHeatmap?: AsyncStateOf<VarianceHeatmapResponse>;
  apiKcphTrend?: AsyncStateOf<KcphTrendResponse>;
  apiLocationTable?: AsyncStateOf<PagedResponse<LocationEfficiencyRow> & { totals?: Record<string, number | null> }>;
  onLocationTableColumnsChange?: (dimensionKeys: string[]) => void;
  locationTablePage?: number;
  locationTablePageSize?: number;
  onLocationTablePageChange?: (page: number) => void;
  locationTablePageSizeOptions?: number[];
  onLocationTablePageSizeChange?: (pageSize: number) => void;
  locationTableSortBy?: string | null;
  locationTableSortDirection?: 'asc' | 'desc';
  onLocationTableSortChange?: (columnKey: string) => void;
  apiEmployeeTable?: AsyncStateOf<PagedResponse<EmployeeEfficiencyRow>>;
  employeeTablePage?: number;
  employeeTablePageSize?: number;
  onEmployeeTablePageChange?: (page: number) => void;
  employeeTablePageSizeOptions?: number[];
  onEmployeeTablePageSizeChange?: (pageSize: number) => void;
  employeeTableSortBy?: string | null;
  employeeTableSortDirection?: 'asc' | 'desc';
  onEmployeeTableSortChange?: (columnKey: string) => void;
  selectedEmployeeLocation?: string | null;
  onLocationSelect?: (locationId: string, locationName: string) => void;
  onClearEmployeeLocation?: () => void;
}

const EfficiencyBreakdown: React.FC<EfficiencyBreakdownProps> = ({
  drillLevel,
  timeframe,
  selectedRegions = [],
  selectedMarkets = [],
  selectedLocations = [],
  selectedColumnKeys,
  apiVarianceHeatmap,
  apiKcphTrend,
  apiLocationTable,
  onLocationTableColumnsChange,
  locationTablePage = 0,
  locationTablePageSize = 25,
  onLocationTablePageChange,
  locationTablePageSizeOptions,
  onLocationTablePageSizeChange,
  locationTableSortBy,
  locationTableSortDirection,
  onLocationTableSortChange,
  apiEmployeeTable,
  employeeTablePage = 0,
  employeeTablePageSize = 25,
  onEmployeeTablePageChange,
  employeeTablePageSizeOptions,
  onEmployeeTablePageSizeChange,
  employeeTableSortBy,
  employeeTableSortDirection,
  onEmployeeTableSortChange,
  selectedEmployeeLocation,
  onLocationSelect,
  onClearEmployeeLocation,
}) => {
  void drillLevel;
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const normalize = (value: string) => value.trim().toLowerCase();
  const timeframeLabel = timeframe.charAt(0).toUpperCase() + timeframe.slice(1);

  // Helper to render loading state
  const renderLoading = () => (
    <View style={[styles.loadingContainer, isDark && { backgroundColor: '#202020' }]}>
      <ActivityIndicator size="large" color="#0B5FC6" />
      <Text style={[styles.loadingText, isDark && { color: '#B0B0B0' }]}>Loading data...</Text>
    </View>
  );

  // Helper to render error state
  const renderError = (title: string, error: string) => (
    <View style={[styles.errorContainer, isDark && { backgroundColor: '#3A1414', borderLeftColor: '#C0392B' }]}>
      <Text style={[styles.errorTitle, isDark && { color: '#F28B82' }]}>{title}</Text>
      <Text style={[styles.errorText, isDark && { color: '#B0B0B0' }]}>{error || 'Unable to load data'}</Text>
    </View>
  );

  // Helper to render empty state
  const renderEmpty = (title: string) => (
    <View style={[styles.emptyContainer, isDark && { backgroundColor: '#202020' }]}>
      <Text style={[styles.emptyTitle, isDark && { color: '#B0B0B0' }]}>{title}</Text>
      <Text style={[styles.emptyText, isDark && { color: '#6E6E6E' }]}>No data available</Text>
    </View>
  );

  // Render chart row (Variance Heatmap & KCPH Trend)
  const renderChartRow = () => {
    const varianceLoading = apiVarianceHeatmap?.loading;
    const varianceError = apiVarianceHeatmap?.error;
    const varianceData = apiVarianceHeatmap?.data;

    const kcphLoading = apiKcphTrend?.loading;
    const kcphError = apiKcphTrend?.error;
    const kcphData = apiKcphTrend?.data;

    if (varianceLoading || kcphLoading) return renderLoading();
    if (varianceError) return renderError('Hours Variance', varianceError);
    if (kcphError) return renderError('KCPH Trend', kcphError);

    if (!varianceData) return renderEmpty('Hours Variance');
    if (!kcphData) return renderEmpty('KCPH Trend');

    const safeDays = Array.isArray(varianceData.days) ? varianceData.days.map((d) => String(d ?? '--')) : [];
    const safeZones = Array.isArray(varianceData.zones) ? varianceData.zones.map((z) => String(z ?? '--')) : [];
    const safeCells = Array.isArray(varianceData.cells) ? varianceData.cells : [];

    const safeKcphData = {
      labels: Array.isArray(kcphData.labels) ? kcphData.labels.map((l) => parseTimeframeLabel(String(l ?? '--'), timeframe).main) : [],
      labelSubLabels: Array.isArray(kcphData.labels) ? kcphData.labels.map((l) => parseTimeframeLabel(String(l ?? '--'), timeframe).sub) : [],
      estimated: Array.isArray(kcphData.estimated) ? kcphData.estimated.map((v) => Number(v ?? 0)) : [],
      scheduled: Array.isArray(kcphData.scheduled) ? kcphData.scheduled.map((v) => Number(v ?? 0)) : [],
      actual: Array.isArray(kcphData.actual) ? kcphData.actual.map((v) => Number(v ?? 0)) : [],
      // Pass an array through as-is so the chart can render a per-period target line once
      // the backend supplies one; a bare number still renders the existing flat line.
      target: Array.isArray(kcphData.target) ? kcphData.target.map((v) => Number(v ?? 90)) : Number(kcphData.target ?? 90),
    };

    return (
      <View style={[styles.chartRow, Platform.OS === 'web' ? null : styles.stackMobile]}>
        <View style={styles.chartCell}>
          <EfficiencyVarianceHeatGridCard
            title="Hours Variance to Model by Zone and Day"
            description="% over(+) / under(-) model"
            columnLabels={safeDays}
            rows={safeZones.map((z, zi) => ({
              label: z,
              values: Array.isArray(safeCells[zi]) ? safeCells[zi].map((v) => Number(v ?? 0)) : [],
            }))}
          />
        </View>
        <View style={styles.chartCell}>
          <KCPHTrendCard
            title="Actual vs Target KCPH Trend"
            subtitle={`by ${timeframeLabel}`}
            description="cases / hr"
            data={safeKcphData}
          />
        </View>
      </View>
    );
  };

  // Render table
  const renderTable = () => {
    const tableLoading = apiLocationTable?.loading;
    const tableError = apiLocationTable?.error;
    const tableData = apiLocationTable?.data;

    if (tableLoading) return renderLoading();
    if (tableError) return renderError('Location Level Efficiency', tableError);

    if (!tableData || !tableData.rows || tableData.rows.length === 0) {
      return renderEmpty('Location Level Efficiency');
    }

    return (
      <LocationLevelEfficiencyTable
        title="Location Level Efficiency"
        selectedColumnKeys={selectedColumnKeys as any}
        onColumnsChange={onLocationTableColumnsChange}
        onLocationSelect={onLocationSelect}
        totals={tableData.totals as any}
        total={tableData.total}
        page={locationTablePage}
        pageSize={locationTablePageSize}
        onPageChange={onLocationTablePageChange}
        pageSizeOptions={locationTablePageSizeOptions}
        onPageSizeChange={onLocationTablePageSizeChange}
        pageLoading={tableLoading}
        sortBy={locationTableSortBy}
        sortDirection={locationTableSortDirection}
        onSortChange={onLocationTableSortChange}
        rows={tableData.rows.map((row) => ({
          region: row.region,
          market: row.market,
          location: row.location,
          locationId: row.locationId,
          year: row.year,
          period: row.period,
          week: row.week,
          category: row.category,
          zone: row.zone,
          forecastedCases: row.forecastedCases,
          actualCases: row.actualCases ?? 0,
          forecastedHours: row.forecastedHours,
          scheduleHours: row.scheduleHours,
          estimatedHours: row.estimatedHours,
          actualHours: row.actualHours,
          siteStandardKcph: row.siteStandardKcph,
          scheduledKcph: row.scheduledKcph,
          actualKcph: row.actualKcph,
          targetKcph: row.targetKcph,
          kcphIndex: row.kcphIndex,
        }))}
      />
    );
  };

  // Render the Employee Level Efficiency drilldown table — always visible, populated once a
  // location has been clicked in the Location Level Efficiency table above.
  const renderEmployeeTable = () => {
    if (selectedEmployeeLocation && apiEmployeeTable?.loading) return renderLoading();
    if (selectedEmployeeLocation && apiEmployeeTable?.error) return renderError('Employee Level Efficiency', apiEmployeeTable.error);

    const employeeData = apiEmployeeTable?.data;

    return (
      <EmployeeLevelEfficiencyTable
        title="Employee Level Efficiency"
        location={selectedEmployeeLocation ?? null}
        rows={(employeeData?.rows ?? []).map((row) => ({
          location: row.location,
          employeeName: row.employeeName,
          employeeGpid: row.employeeGpid,
          role: row.role,
          year: row.year,
          period: row.period,
          week: row.week,
          category: row.category,
          zone: row.zone,
          estimatedKcph: row.estimatedKcph,
          scheduledKcph: row.scheduledKcph,
          actualKcph: row.actualKcph,
        }))}
        total={employeeData?.total}
        page={employeeTablePage}
        pageSize={employeeTablePageSize}
        onPageChange={onEmployeeTablePageChange}
        pageSizeOptions={employeeTablePageSizeOptions}
        onPageSizeChange={onEmployeeTablePageSizeChange}
        pageLoading={apiEmployeeTable?.loading}
        sortBy={employeeTableSortBy}
        sortDirection={employeeTableSortDirection}
        onSortChange={onEmployeeTableSortChange}
      />
    );
  };

  return (
    <View style={styles.container}>
      {renderChartRow()}
      {renderTable()}
      {renderEmployeeTable()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 16,
  },
  loadingContainer: {
    padding: 40,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    minHeight: 200,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: '#666',
  },
  errorContainer: {
    padding: 20,
    backgroundColor: '#fff3e0',
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#ff9800',
  },
  errorTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#d32f2f',
  },
  errorText: {
    fontSize: 12,
    color: '#999',
    marginTop: 4,
  },
  emptyContainer: {
    padding: 30,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fafafa',
    borderRadius: 8,
    minHeight: 150,
  },
  emptyTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#999',
  },
  emptyText: {
    fontSize: 12,
    color: '#bbb',
    marginTop: 4,
  },
  chartRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  stackMobile: {
    flexDirection: 'column',
  },
  chartCell: {
    flex: 1,
    minHeight: 360,
    overflow: 'hidden',
    maxWidth: '100%',
    maxHeight: 420,
  },

});

export default EfficiencyBreakdown;
